import React, { useState } from 'react';
import { Upload, Camera, X, Loader } from 'lucide-react';
import { processReceiptFile } from '../../services/receiptProcessing';

const PDF_ATTACHED = 'pdf-attached';

const ReceiptUpload = ({ onReceiptProcessed, onReceiptCleared }) => {
    const [uploading, setUploading] = useState(false);
    const [ocrProgress, setOcrProgress] = useState(0);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await processFile(file);
    };

    const processFile = async (file) => {
        setError(null);
        setUploading(true);
        setOcrProgress(0);
        const result = await processReceiptFile(file, setOcrProgress);
        setUploading(false);

        if (result.error) { // cannot be attached at all
            setError(result.error);
            return;
        }
        setPreview(result.previewUrl || PDF_ATTACHED);
        onReceiptProcessed({ ...(result.ocr || {}), receiptUrl: result.receiptUrl });
        if (result.warning) setError(result.warning);
    };

    const clearReceipt = () => {
        setPreview(null);
        setError(null);
        setOcrProgress(0);
        if (onReceiptCleared) onReceiptCleared();
    };

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Receipt
            </label>

            {!preview ? (
                <div style={{
                    border: '2px dashed var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '2rem',
                    textAlign: 'center',
                    background: 'var(--card-bg)'
                }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <Upload size={48} style={{ color: 'var(--text-muted)' }} />
                    </div>

                    <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
                        Upload or capture receipt
                    </p>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            <Upload size={16} style={{ marginRight: '0.5rem' }} />
                            Upload File
                            <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </label>

                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            <Camera size={16} style={{ marginRight: '0.5rem' }} />
                            Take Photo
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </label>
                    </div>
                </div>
            ) : (
                <div style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '1rem',
                    background: 'var(--card-bg)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <span style={{ fontWeight: 600 }}>Receipt Preview</span>
                        <button
                            type="button"
                            aria-label="Remove receipt"
                            onClick={clearReceipt}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--text-muted)',
                                padding: '0.25rem'
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {preview === PDF_ATTACHED ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>PDF attached (no preview available)</div>
                    ) : (
                        <img
                            src={preview}
                            alt="Receipt preview"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '600px',
                                objectFit: 'contain',
                                borderRadius: 'var(--radius)'
                            }}
                        />
                    )}

                    {uploading && (
                        <div style={{ marginTop: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <Loader size={16} className="spin" />
                                <span>
                                    {preview?.startsWith('data:application/pdf')
                                        ? `Processing PDF... ${Math.round(ocrProgress)}%`
                                        : `Processing receipt... ${Math.round(ocrProgress)}%`}
                                </span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '4px',
                                background: 'var(--border)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${ocrProgress}%`,
                                    height: '100%',
                                    background: 'var(--primary)',
                                    transition: 'width 0.3s'
                                }} />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div style={{
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: 'var(--radius)',
                    color: '#c00'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default ReceiptUpload;
